import React, { useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import styles from './styles.module.scss';
import { ArrowUpIcon, ChartIcon, HourglassTwoIcon } from 'assets';
import { useManageJobsDashboard } from 'hooks';
import { useCredits } from 'hooks/useCredits';
import { Spinner } from 'components/loader';
import { BuyAdditionalCreditModal } from 'pages/pricing-v2/components/buy-additional-credit-modal';
import { getRoleSourceLabel } from 'utils/roleSource';

ChartJS.register(ArcElement, Tooltip, Legend);

// DoughnutChartExtended component remains mostly unchanged
interface DoughnutChartProps {
  score?: number;
  data?: any;
  options?: any;
  className?: string;
  cutout?: string;
  showCenterText?: boolean;
  showRoleCenterText?: boolean;
  centerText?: string;
  divisorText?: string;
  centerSubText?: string;
  thickArc?: boolean;
}

const DoughnutChartExtended: React.FC<DoughnutChartProps> = ({
  score,
  data,
  options,
  className = '',
  cutout = "85%",
  showCenterText = false,
  showRoleCenterText = false,
  centerText,
  divisorText,
  centerSubText,
  thickArc = false
}) => {
  const chartData = data || {
    datasets: [
      {
        data: [score || 0, 100 - (score || 0)],
        borderColor: "transparent",
        backgroundColor: [
          (score || 0) >= 90
            ? "#18B368"
            : (score || 0) >= 70
              ? "#1570EF"
              : (score || 0) >= 50
                ? "#EDA12F"
                : "#E74444",
          (score || 0) >= 90
            ? "#18B36810"
            : (score || 0) >= 70
              ? "#1570EF10"
              : (score || 0) >= 50
                ? "#EDA12F10"
                : "#E7444410",
        ],
        hoverOffset: 0,
        borderAlign: "inner",
      },
    ],
  };

  const chartOptions = options || {
    responsive: true,
    maintainAspectRatio: false,
    cutout: cutout,
    animation: {
      animateRotate: true,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: { enabled: true },
    },
  };

  const thickArcPlugin = {
    id: "thickArc",
    afterDatasetDraw(chart: any) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const arc = meta.data[0];
      if (!arc) return;

      const lineWidth = 35; // thickness
      ctx.save();
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = arc.options.backgroundColor;
      ctx.beginPath();
      ctx.arc(
        arc.x,
        arc.y,
        arc.outerRadius - lineWidth / 3,
        arc.startAngle,
        arc.endAngle
      );
      ctx.stroke();
      ctx.restore();
    },
  };


  return (
    <div className={styles.chartContainer}>
      <Doughnut data={chartData} options={chartOptions} className={className} plugins={thickArc ? [thickArcPlugin] : []} />
      {showCenterText && (
        <div className={styles.chartCenterText}>
          <div className={styles.chartMainText}>
            {centerText}
          </div>
          {centerSubText && (
            <div className={styles.chartSubText}>
              {centerSubText}
            </div>
          )}
        </div>
      )}
      {showRoleCenterText && (() => {
        const totalLength = (centerText || '').length + (divisorText || '').length + 1; // +1 for "/"
        const mainSize = totalLength >= 9 ? 16 : totalLength >= 7 ? 20 : totalLength >= 5 ? 26 : 36;
        const subSize = totalLength >= 9 ? 10 : totalLength >= 7 ? 12 : totalLength >= 5 ? 14 : 16;
        return (
          <span className={`${styles.metricValue} ${styles.chartCenterText}`} style={{ fontSize: mainSize }}>
            {centerText}
            <span className={styles.roleTotal} style={{ fontSize: subSize }}>/{divisorText}</span>
          </span>
        );
      })()}
    </div>
  );
};

// Statistics Dashboard Component
const ManageJobsStats: React.FC = () => {
  const { dashboardData, isLoading } = useManageJobsDashboard();
  const { credits: creditsSnapshot } = useCredits();
  const [showBuyCreditModal, setShowBuyCreditModal] = useState(false);

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      </div>
    );
  }

  // Default values if data is not available
  const totalTalents = dashboardData?.total_talents || 0;
  const sources = dashboardData?.sources || [];
  
  // Parse average time spent (e.g., "45 mins" or "1 hr 20 mins")
  const avgTimeValue = dashboardData?.avg_time_spent?.value || "0 mins";
  const avgTimeGrowth = dashboardData?.avg_time_spent?.growth || 0;
  
  // Parse completion rate
  const completionRate = dashboardData?.completion_rate?.percentage || 0;
  const completionGrowth = dashboardData?.completion_rate?.growth || 0;
  
  // Credit balance — measured against the account's current ceiling (weekly
  // allowance + purchased) so the ring reflects what is actually spendable.
  const creditsAvailable = creditsSnapshot?.totalAvailable || 0;
  const creditsTotal = creditsSnapshot?.spendableCeiling || 0;
  const creditsUsed = Math.max(0, creditsTotal - creditsAvailable);
  const isRunningLow = creditsSnapshot?.runningLow || false;

  const SOURCE_COLORS: Record<string, string> = {
    job_board: '#16C7B7',
    referral: '#EFD8C5',
    social_media: '#EE950C',
    company_website: '#E5003B',
    recruiter: '#FF5A7A',
    linkedin: '#4C8DFF',
    whatsapp: '#31D67B',
    twitter: '#5BC0FF',
    indeed: '#FF6B35',
    precision: '#605BFF',
    other: '#B8B8BE',
  };

  const getSourceColor = (name: string) => {
    const normalized = name.toLowerCase().replace(/\s+/g, '_');
    return SOURCE_COLORS[normalized] ?? '#B8B8BE';
  };

  const sourceData = sources.map(s => s.count);
  const sourceBackgrounds = sources.map((source) => {
    if (totalTalents <= 0) return '#E5E5E5';
    return getSourceColor(source.name);
  });

  // Chart data
  const totalTalentsData = {
    datasets: [{
      data: sourceData.length > 0 ? sourceData : [1], // Show at least something if empty
      backgroundColor: sourceBackgrounds.length > 0 ? sourceBackgrounds : ['#E5E5E5'],
      borderWidth: 0,
      borderRadius: 4,
      spacing: 8,
      borderAlign: "inner" as const,
      hoverOffset: 4,
    }]
  };

  const creditPercentage = creditsTotal > 0 ? (creditsAvailable / creditsTotal) * 100 : 0;
  const creditColor = creditPercentage >= 90 ? '#18B368'
    : creditPercentage >= 70 ? '#1570EF'
    : creditPercentage >= 50 ? '#EDA12F'
    : '#E74444';

  const creditChartData = {
    datasets: [{
      data: [
        creditsAvailable,  // remaining
        creditsUsed,       // used
      ],
      backgroundColor: [creditColor, '#E5E5E5'],
      borderWidth: 0,
      hoverOffset: 0,
    }]
  };

  const chartOptions = {
    responsive: true,
    // maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
  };

  const chartOptionsTwo = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 10,
    },
    cutout: "70%",
    rotation: 180,
    circumference: 360,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
  };

  return (
    <>
    <div className={styles.container}>
      {/* Total Talents & Sources */}
      <div className={styles.card}>
        <h3 className={styles.title}>Total Talents & Sources</h3>
        <div className={styles.flexColumn}>
          <div className={styles.chartContainer}>
            <DoughnutChartExtended
              data={totalTalentsData}
              options={chartOptions}
              cutout="75%"
              showCenterText={true}
              centerText={totalTalents.toString()}
              centerSubText=""
              className={styles.totalDoughnut}
            />
          </div>
          <div>
            <div className={styles.flexRow}>
              {sources.map((source, index) => (
                <div key={source.name} className={styles.flexCenter}>
                  <div 
                    className={styles.legendItem} 
                    style={{ backgroundColor: sourceBackgrounds[index] }}
                  ></div>
                  <span className={styles.legendText}>
                    <strong className={styles.legendValue}>
                      {source.count}
                    </strong> {getRoleSourceLabel(source.name)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Average Time & Completion Rate */}
      <div className={styles.cardFlex}>
        {/* Avg Time Spent Applying */}
        <div className={`lg:h-auto h-full ${styles.card}`}>
          <div className={`lg:w-auto w-full lg:flex-row flex-col ${styles.flexRowStart}`}>
            <div className={styles.metricIcon}>
              <HourglassTwoIcon />
            </div>
            <div className={`lg:mx-0 mx-auto lg:mb-0 mb-4 ${styles.flexColumnStart}`}>
              <h3 className={`lg:mx-0 mx-auto ${styles.subTitle}`}>Avg Time Spent Applying</h3>
              <div className={`lg:mx-0 mx-auto ${styles.metricContainer}`}>
                <span className={styles.metricValue}>
                  {avgTimeValue}
                </span>
              </div>

              <div className={`lg:mx-0 mx-auto ${styles.flexCenter}`}>
                <span className={`${styles.percentageChange} ${avgTimeGrowth >= 0 ? styles.percentageIncrease : styles.percentageDecrease}`}>
                  {avgTimeGrowth >= 0 ?
                    <span className={styles.increaseArrow}>
                      <ArrowUpIcon />
                    </span> :
                    <span className={styles.decreaseArrow}>
                      <ArrowUpIcon className={styles.arrow} />
                    </span>} {avgTimeGrowth >= 0 ? '+' : ''}{avgTimeGrowth.toFixed(2)}%
                </span>
                <span className={styles.timePeriod}>this month</span>
              </div>
            </div>
          </div>
        </div>

        {/* Completion Rate */}
        <div className={`lg:h-auto h-full ${styles.card}`}>
          <div className={`lg:w-auto w-full lg:flex-row flex-col ${styles.flexRowStart}`}>
            <div className={styles.metricIcon}>
              <ChartIcon />
            </div>
            <div className={`lg:mx-0 mx-auto lg:mb-0 mb-4 ${styles.flexColumnStart}`}>
              <h3 className={`lg:mx-0 mx-auto ${styles.subTitle}`}>Completion rate</h3>
              <div className={`lg:mx-0 mx-auto ${styles.metricContainer}`}>
                <span className={styles.metricValue}>
                  {completionRate.toFixed(2)}%
                </span>
              </div>

              <div className={`lg:mx-0 mx-auto ${styles.flexCenter}`}>
                <span className={`${styles.percentageChange} ${completionGrowth >= 0 ? styles.percentageIncrease : styles.percentageDecrease}`}>
                  {completionGrowth >= 0 ?
                    <span className={styles.increaseArrow}>
                      <ArrowUpIcon />
                    </span> :
                    <span className={styles.decreaseArrow}>
                      <ArrowUpIcon className={styles.arrow} />
                    </span>} {completionGrowth >= 0 ? '+' : ''}{completionGrowth.toFixed(2)}%
                </span>
                <span className={`whitespace-nowrap ${styles.timePeriod}`}>this month</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credit Balance */}
      <div className={styles.card}>
        <h3 className={styles.title}>Credit Balance</h3>
        <div className={`${styles.flexColumnCenter} ${styles.roleCounntsSection}`}>
          <div className={styles.chartDoughnutContainer}>
            <DoughnutChartExtended
              data={creditChartData}
              options={chartOptionsTwo}
              thickArc={true}
              cutout="85%"
              showRoleCenterText={true}
              centerText={`${creditsAvailable}`}
              divisorText={`${creditsTotal}`}
            />
          </div>

          <div className={styles.flexColumnCenter}>
            {isRunningLow && (
              <div className={styles.warningText}>
                You are running out of credits
              </div>
            )}
            <button className={`${!isRunningLow && '!mt-10'} ${styles.buyButton}`} onClick={() => setShowBuyCreditModal(true)}>
              Topup credit
            </button>
          </div>
        </div>
      </div>
    </div>

    <BuyAdditionalCreditModal
      isOpen={showBuyCreditModal}
      onClose={() => setShowBuyCreditModal(false)}
      currentBalance={creditsAvailable}
    />
  </>
  );
};

export { ManageJobsStats, DoughnutChartExtended };
